import type {
	CoreApiError,
	IUserImportCommitRequest,
	IUserImportCommitResponse,
	IUserImportValidateRequest,
	IUserImportValidateResponse,
} from '@lqa-frontend/api-services';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
	useInvalidateAfterUserWrite,
} from '@lqa-frontend/api-services';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

export function useUserImportValidateMutation(
	options?: UseUserImportValidateMutationOptions
) {
	return useMutation({
		mutationKey: coreApiQueryKey(
			endpoints.userSettings.users.importValidate,
			'post'
		),
		mutationFn: async (request: IUserImportValidateRequest) =>
			coreApiClient
				.post<IUserImportValidateResponse>(
					endpoints.userSettings.users.importValidate,
					request
				)
				.then((response) => response.data),
		...options,
	});
}

export function useUserImportCommitMutation(
	options?: UseUserImportCommitMutationOptions
) {
	const invalidateAfterWrite = useInvalidateAfterUserWrite();

	return useMutation({
		mutationKey: coreApiQueryKey(endpoints.userSettings.users.import, 'post'),
		mutationFn: async (request: IUserImportCommitRequest) =>
			coreApiClient
				.post<IUserImportCommitResponse>(
					endpoints.userSettings.users.import,
					request
				)
				.then((response) => response.data),
		...options,
		onSuccess: async (data, variables, onMutateResult, context) => {
			await invalidateAfterWrite();
			options?.onSuccess?.(data, variables, onMutateResult, context);
		},
	});
}

type UseUserImportValidateMutationOptions = UseMutationOptions<
	IUserImportValidateResponse,
	AxiosError<CoreApiError>,
	IUserImportValidateRequest
>;

type UseUserImportCommitMutationOptions = UseMutationOptions<
	IUserImportCommitResponse,
	AxiosError<CoreApiError>,
	IUserImportCommitRequest
>;
